import type { ExportsXmpMetadata } from '@frameio/graphql-client/generated';

/**
 * Type guard to check if an ExportsXmpMetadata has a shareId property.
 * This is needed because V3PanelExportsXmpMetadata doesn't have shareId,
 * while SequenceExportsXmpMetadata and V4CommentsPanelExportsXmpMetadata do.
 */
export function hasShareId(
  metadata: ExportsXmpMetadata | null | undefined
): metadata is ExportsXmpMetadata & { shareId?: string } {
  return metadata != null && 'shareId' in metadata;
}

/**
 * Type guard to check if an ExportsXmpMetadata has a shareId property with a non-empty value.
 */
export function hasValidShareId(
  metadata: ExportsXmpMetadata | null | undefined
): metadata is ExportsXmpMetadata & { shareId: string } {
  return hasShareId(metadata) && Boolean(metadata.shareId);
}

/**
 * Converts a number of seconds to a timecode string (HH:MM:SS:FF)
 *
 * @param {number} seconds - The number of seconds to convert.
 * @param {number} frameRate - The frame rate to use. Defaults to 24. Could be NaN.
 * @returns The timecode string.
 */
export function secondsToTimecode(seconds: number, frameRate: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const frames = Math.floor((seconds % 1) * (frameRate || 24));

  const pad = (num: number) => String(num).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}:${pad(frames)}`;
}

/**
 * Convert a file size in bytes into a human-readable string (KB, MB, GB, etc.).
 *
 * @param {number} bytes - The file size in bytes.
 * @param {number} [decimals=2] - Number of decimal places to include in the formatted result.
 * @returns {string} A formatted string representing the size with the appropriate unit.
 *
 * @example
 * formatBytes(500);          // "500 Bytes"
 * formatBytes(10240);        // "10 KB"
 * formatBytes(1048576);      // "1 MB"
 * formatBytes(1234567890);   // "1.15 GB"
 */
export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
}
